import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/layout/AppLayout';
import { ROUTES } from '../constants/constants';
import { usersAPI, rolesAPI, userFlagsAPI } from '../services/api';
import { getView, setView, hasView } from '../services/viewCache';
import {
  FiSettings, FiShield,
  FiMail, FiPhone, FiBriefcase, FiAward, FiUser, FiHash, FiFileText, FiMapPin,
  FiClock, FiCalendar, FiCheckCircle, FiUserX, FiUserCheck, FiLogOut, FiTrash2, FiX, FiLock, FiInfo,
  FiChevronLeft, FiChevronRight, FiFlag,
} from 'react-icons/fi';
import EntityAuditHistory from '../components/admin/EntityAuditHistory';
import ViewButton from '../components/common/ViewButton';
import RefreshButton from '../components/common/RefreshButton';
import FlagUserModal from '../components/admin/FlagUserModal';
import FlagHistorySection from '../components/admin/FlagHistorySection';
import Button from '../components/common/Button';

// Stale-while-revalidate keys (services/viewCache): this route unmounts on nav,
// so the user table reloaded from zero on return and showed "Loading users..."
// over rows the admin just saw. The list key folds in page + every filter.
const usersKey = (page, search, role, status) =>
  `admin:users:${page}|${search}|${role}|${status}`;
const ROLES_KEY = 'admin:users:roles';

function ConfirmModal({ open, title, message, confirmLabel, danger, onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" style={{ background: 'rgba(8,17,31,0.55)' }} onClick={onCancel}>
      <div className="bg-white rounded-[12px] p-6 max-w-[400px] w-full mx-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-[16px] font-bold text-[#08111f] mb-2">{title}</h3>
        <p className="text-[13px] text-[#7a7060] mb-5">{message}</p>
        <div className="flex gap-3 justify-end">
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm}>{confirmLabel || 'Confirm'}</Button>
        </div>
      </div>
    </div>
  );
}

function InfoField({ icon, label, value, mono = false }) {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1 text-[9.5px] uppercase tracking-wider text-[#7a7060] mb-1">
        <span className="text-[10px] text-[#145f5f]">{icon}</span>
        <span>{label}</span>
      </div>
      <div
        className="text-[12.5px] font-semibold text-[#08111f] truncate"
        style={mono ? { fontFamily: "'JetBrains Mono', monospace" } : undefined}
        title={typeof value === 'string' ? value : undefined}
      >
        {value || <span className="text-[#b8b0a0] font-normal italic">Not provided</span>}
      </div>
    </div>
  );
}

function SectionHeader({ children }) {
  return (
    <h4 className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#7a7060] mb-2 flex items-center gap-1.5 px-0.5">
      <span className="w-[3px] h-3 rounded-full" style={{ background: '#145f5f' }} />
      {children}
    </h4>
  );
}

const PRIVILEGED_ROLES = ['admin', 'super_admin'];

function UserDetailModal({ user, roles, onClose, onAction, viewerRoleName, viewerId, activeSuperAdminCount, onUserUpdated }) {
  const [selectedRole, setSelectedRole] = useState('');
  const [actionBusy, setActionBusy] = useState('');
  const [flagModalOpen, setFlagModalOpen] = useState(false);
  const [openFlagCount, setOpenFlagCount] = useState(0);
  const [totalFlagCount, setTotalFlagCount] = useState(0);
  // Start collapsed — the count badge in the label is signal enough; admins
  // expand the panel only when they want to read the actual entries.
  const [showFlagHistory, setShowFlagHistory] = useState(false);

  // Sync selectedRole when user changes
  useEffect(() => {
    if (user) setSelectedRole(user.roleName || '');
  }, [user]);

  // Pull flag totals up front so the header badge can show counts even
  // while the panel itself stays collapsed. Total = lifetime flags filed
  // (open + cleared + compromised); Open = still needs super-admin review.
  useEffect(() => {
    if (!user?._id && !user?.id) return;
    const uid = user._id || user.id;
    userFlagsAPI
      .listForUser(uid)
      .then((res) => {
        const flags = res?.data?.flags || [];
        setTotalFlagCount(flags.length);
        setOpenFlagCount(flags.filter((f) => f.status === 'open').length);
      })
      .catch(() => { setTotalFlagCount(0); setOpenFlagCount(0); });
  }, [user?._id, user?.id]);

  const handleAction = async (action, payload) => {
    setActionBusy(action);
    try { await onAction(action, user._id || user.id, payload); }
    finally { setActionBusy(''); }
  };

  if (!user) return null;

  const initials = (user.fullName || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join('')
    .toUpperCase();
  const isActive = user.isActive !== false;
  const hasProfessionalDetails = !!(user.companyName || user.designation || user.profession || user.membershipNo || user.cin || user.state);

  // Role-change authority: only super admins can grant/revoke admin or super_admin roles
  const viewerIsSuperAdmin = viewerRoleName === 'super_admin';
  const viewerIsSelf = !!viewerId && (user._id === viewerId || user.id === viewerId);
  const targetIsPrivileged = PRIVILEGED_ROLES.includes(user.roleName);
  const cannotTouchPrivilegedTarget = targetIsPrivileged && !viewerIsSuperAdmin;
  const sectionLocked = cannotTouchPrivilegedTarget;

  // Last-super-admin protection: cannot demote / deactivate / delete the sole active super admin.
  const isLastSuperAdmin =
    user.roleName === 'super_admin' &&
    isActive &&
    typeof activeSuperAdminCount === 'number' &&
    activeSuperAdminCount <= 1;

  const isRoleOptionLocked = (roleName) => {
    if (!viewerIsSuperAdmin && PRIVILEGED_ROLES.includes(roleName)) return true;
    if (isLastSuperAdmin && roleName !== 'super_admin') return true;
    return false;
  };
  const updateRoleDisabled =
    sectionLocked ||
    actionBusy === 'changeRole' ||
    selectedRole === user.roleName ||
    isRoleOptionLocked(selectedRole);

  // Per-button reason for being disabled (also drives the tooltip text)
  const deactivateBlockReason = (() => {
    if (viewerIsSelf && isActive) return "You can't deactivate your own account";
    if (isActive && isLastSuperAdmin) return 'Cannot deactivate the last active super admin';
    if (cannotTouchPrivilegedTarget) return 'Only super admins can deactivate an admin or super admin';
    return null;
  })();
  const deleteBlockReason = (() => {
    if (viewerIsSelf) return "You can't delete your own account";
    if (isLastSuperAdmin) return 'Cannot delete the last active super admin';
    if (cannotTouchPrivilegedTarget) return 'Only super admins can delete an admin or super admin';
    return null;
  })();
  const revokeBlockReason = (() => {
    // Self-revoke ("sign me out everywhere") is always allowed
    if (viewerIsSelf) return null;
    if (cannotTouchPrivilegedTarget) return "Only super admins can revoke another admin's sessions";
    return null;
  })();
  const deactivateDisabled = !!actionBusy || !!deactivateBlockReason;
  const deleteDisabled = !!actionBusy || !!deleteBlockReason;
  const revokeDisabled = !!actionBusy || !!revokeBlockReason;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: 'rgba(8,17,31,0.55)' }} onClick={onClose}>
      <div
        className="bg-white rounded-[16px] max-w-[960px] w-full shadow-2xl max-h-[92vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{ boxShadow: '0 25px 50px -12px rgba(8,17,31,.25), 0 0 0 1px rgba(221,214,198,.4)' }}
      >
        {/* Hero header */}
        <div
          className="relative px-6 py-3 border-b shrink-0"
          style={{ borderColor: '#e8e2d6', background: 'linear-gradient(135deg, #f8f5ef 0%, #fdfbf6 55%, #ffffff 100%)' }}
        >
          {/* Subtle decorative circle */}
          <div
            className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-30 pointer-events-none"
            style={{ background: 'radial-gradient(circle, #145f5f15 0%, transparent 70%)', transform: 'translate(30%, -30%)' }}
          />

          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center text-[#7a7060] hover:bg-white hover:text-[#08111f] hover:shadow-sm transition-all z-10"
          >
            <FiX className="text-[16px]" />
          </button>

          <div className="flex items-start gap-4 relative">
            {/* Avatar */}
            <div className="relative shrink-0">
              <div
                className="w-[64px] h-[64px] rounded-full flex items-center justify-center text-[22px] font-bold text-white shadow-md"
                style={{ background: 'linear-gradient(135deg, #1a7373 0%, #145f5f 60%, #0d4848 100%)', fontFamily: "'Cormorant Garamond', serif", letterSpacing: '0.02em' }}
              >
                {initials || <FiUser className="text-[26px]" />}
              </div>
              <span
                className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border-[2.5px] border-white"
                style={{ background: isActive ? '#4a6b3f' : '#9a564a' }}
                title={isActive ? 'Active' : 'Inactive'}
              />
            </div>

            <div className="flex-1 min-w-0 pr-8">
              <h3 className="text-[22px] font-bold text-[#08111f] leading-tight truncate" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                {user.fullName || 'Unnamed User'}
              </h3>
              <p className="text-[12.5px] text-[#7a7060] truncate mt-0.5">{user.email}</p>
              <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
                <span
                  className="inline-flex items-center gap-1 px-2 py-[3px] rounded-full text-[10px] font-semibold capitalize"
                  style={{ background: '#145f5f18', color: '#145f5f' }}
                >
                  <FiShield className="text-[10px]" />
                  {(user.roleName || 'user').replace(/_/g, ' ')}
                </span>
                <span className={`inline-flex items-center gap-1 px-2 py-[3px] rounded-full text-[10px] font-semibold ${isActive ? 'bg-[#e8efe5] text-[#4a6b3f]' : 'bg-[#f2e9e6] text-[#9a564a]'}`}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: isActive ? '#4a6b3f' : '#9a564a' }} />
                  {isActive ? 'Active' : 'Inactive'}
                </span>
                {user.isPhoneVerified && (
                  <span className="inline-flex items-center gap-1 px-2 py-[3px] rounded-full text-[10px] font-semibold bg-[#e8efe5] text-[#4a6b3f]">
                    <FiCheckCircle className="text-[10px]" />
                    Verified
                  </span>
                )}
                {isLastSuperAdmin && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-[3px] rounded-full text-[10px] font-bold"
                    style={{ background: '#f3ebd7', color: '#8a6a1f', border: '1px solid #e7d7ad' }}
                    title="This is the only active super admin. Cannot be demoted, deactivated, or deleted."
                  >
                    <FiLock className="text-[10px]" />
                    Last Super Admin
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Body — two columns when Flag History is expanded; single column
            (user details fill the whole width) when it's collapsed. A small
            re-open pill stays in the right column header so the panel is
            never out of reach. */}
        <div className="flex-1 flex flex-col md:flex-row overflow-hidden" style={{ background: '#fdfcf9' }}>
          {/* Left column — only rendered when expanded. Hidden state hands the
              full width to the user details on the right. */}
          {showFlagHistory && (
            <aside
              className="w-full md:w-[320px] md:border-r overflow-y-auto cs-content-scroll px-3 py-4 shrink-0"
              style={{ borderColor: '#e8e2d6', background: '#fbf8f0' }}
            >
              <button
                type="button"
                onClick={() => setShowFlagHistory(false)}
                aria-expanded
                title="Hide flag history — gives the user details more room"
                className="w-full flex items-center gap-1.5 mb-2 text-left hover:opacity-80 transition-opacity"
              >
                <FiFlag className="text-[12px] text-[#b8973a] shrink-0" />
                <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#7a7060]">Flag History</span>
                {totalFlagCount > 0 && (
                  <span className="inline-flex items-center px-1.5 py-[1px] rounded-full text-[9px] font-bold" style={{ background: '#f0ebe0', color: '#5a4e3a' }}>
                    {totalFlagCount}
                  </span>
                )}
                {openFlagCount > 0 && (
                  <span className="inline-flex items-center px-1.5 py-[1px] rounded-full text-[9px] font-bold" style={{ background: '#f3ebd7', color: '#8a6a1f' }}>
                    {openFlagCount} OPEN
                  </span>
                )}
                <FiChevronLeft className="text-[12px] text-[#9a9080] ml-auto" />
              </button>
              {!viewerIsSelf && (
                <Button
                  variant="warning"
                  size="sm"
                  block
                  leftIcon={<FiFlag className="text-[11px]" />}
                  className="mb-3"
                  title="Open the flag form so a super admin can review this account"
                  onClick={() => setFlagModalOpen(true)}
                >
                  Flag for Super Admin Review
                </Button>
              )}
              <FlagHistorySection
                userId={user._id || user.id}
                currentUserRole={viewerRoleName}
                onUserUpdated={onUserUpdated}
                readOnly
              />
            </aside>
          )}

          {/* Right column — main details + audit history. When the left column
              is hidden, this fills the whole modal width. */}
          <div className="flex-1 overflow-y-auto cs-content-scroll px-6 py-5 space-y-5">
            {/* Top-of-page toggles — discoverability without using vertical
                space. Flag History pill is only here when the left column is
                collapsed; Audit History sits next to it as its own collapsible
                bar so admins can pop it open without scrolling to the bottom. */}
            {!showFlagHistory && (
              <div className="flex items-center flex-wrap gap-2 -mt-1">
                <button
                  type="button"
                  onClick={() => setShowFlagHistory(true)}
                  title={totalFlagCount === 0
                    ? 'No flags filed against this user yet — click to view section'
                    : `${totalFlagCount} flag${totalFlagCount === 1 ? '' : 's'} filed against this user${openFlagCount > 0 ? `, ${openFlagCount} still open` : ''}`}
                  className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-md border transition-colors hover:bg-white"
                  style={{ borderColor: '#e8e2d6', color: '#7a7060', background: '#fdfbf6' }}
                >
                  <FiFlag className="text-[11px] text-[#b8973a]" />
                  Flag History
                  {totalFlagCount > 0 && (
                    <span className="inline-flex items-center px-1.5 py-[1px] rounded-full text-[9px] font-bold" style={{ background: '#f0ebe0', color: '#5a4e3a' }}>
                      {totalFlagCount}
                    </span>
                  )}
                  {openFlagCount > 0 && (
                    <span className="inline-flex items-center px-1.5 py-[1px] rounded-full text-[9px] font-bold" style={{ background: '#f3ebd7', color: '#8a6a1f' }}>
                      {openFlagCount} OPEN
                    </span>
                  )}
                  <FiChevronRight className="text-[11px] text-[#9a9080]" />
                </button>
                {!viewerIsSelf && (
                  <Button
                    variant="warning"
                    size="sm"
                    leftIcon={<FiFlag className="text-[11px]" />}
                    title="Open the flag form so a super admin can review this account"
                    onClick={() => setFlagModalOpen(true)}
                  >
                    Flag for Super Admin Review
                  </Button>
                )}
              </div>
            )}

            {/* Audit History — moved to the top right of the modal as a
                collapsible bar. Click the header to expand/collapse the
                event list. Starts collapsed (defaultOpen unset). */}
            <EntityAuditHistory entityType="User" entityId={user._id || user.id} limit={20} />

            {/* Contact */}
            <section>
              <SectionHeader>Contact</SectionHeader>
              <div className="grid grid-cols-2 gap-x-4 gap-y-3 p-4 rounded-[12px] border bg-white" style={{ borderColor: '#e8e2d6' }}>
                <InfoField icon={<FiMail />} label="Email" value={user.email} />
                <InfoField icon={<FiPhone />} label="Mobile" value={user.mobile} mono />
              </div>
            </section>

            {/* Professional Details */}
            {hasProfessionalDetails && (
              <section>
                <SectionHeader>Professional Details</SectionHeader>
                <div className="grid grid-cols-2 gap-x-4 gap-y-3 p-4 rounded-[12px] border bg-white" style={{ borderColor: '#e8e2d6' }}>
                  {user.companyName && <InfoField icon={<FiBriefcase />} label="Company" value={user.companyName} />}
                  {user.profession && <InfoField icon={<FiAward />} label="Profession" value={user.profession} />}
                  {user.designation && <InfoField icon={<FiUser />} label="Designation" value={user.designation} />}
                  {user.membershipNo && <InfoField icon={<FiHash />} label="Membership No." value={user.membershipNo} mono />}
                  {user.cin && <InfoField icon={<FiFileText />} label="CIN" value={user.cin} mono />}
                  {user.state && <InfoField icon={<FiMapPin />} label="State" value={user.state} />}
                </div>
              </section>
            )}

            {/* Activity */}
            <section>
              <SectionHeader>Activity</SectionHeader>
              <div className="grid grid-cols-2 gap-x-4 gap-y-3 p-4 rounded-[12px] border bg-white" style={{ borderColor: '#e8e2d6' }}>
                <InfoField
                  icon={<FiClock />}
                  label="Last Login"
                  value={user.lastLogin ? new Date(user.lastLogin).toLocaleString('en-IN') : (
                    <span className="text-[#b8b0a0] font-normal italic">Never</span>
                  )}
                />
                <InfoField
                  icon={<FiCalendar />}
                  label="Registered"
                  value={user.createdAt ? new Date(user.createdAt).toLocaleString('en-IN') : '-'}
                />
              </div>
            </section>
          </div>
        </div>

        {/* Sticky footer - Admin Actions */}
        <div className="px-6 py-4 border-t bg-white shrink-0" style={{ borderColor: '#e8e2d6', boxShadow: '0 -4px 12px rgba(8,17,31,.04)' }}>
          <h4 className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#7a7060] mb-3 flex items-center gap-1.5">
            <FiSettings className="text-[11px] text-[#145f5f]" />
            Admin Actions
          </h4>

          {/* Change Role row */}
          {isLastSuperAdmin ? (
            <div
              className="mb-2.5 px-3 py-2 rounded-[9px] border flex items-start gap-2 text-[11px]"
              style={{ background: '#f3ebd7', borderColor: '#e7d7ad', color: '#8a6a1f' }}
            >
              <FiLock className="text-[12px] mt-[1px] shrink-0" />
              <span>
                <strong>Last active super admin.</strong> Promote another user to super admin first
                before demoting, deactivating, or deleting this account.
              </span>
            </div>
          ) : sectionLocked && (
            <div
              className="mb-2.5 px-3 py-2 rounded-[9px] border flex items-start gap-2 text-[11px]"
              style={{ background: '#f3ebd7', borderColor: '#e7d7ad', color: '#8a6a1f' }}
            >
              <FiLock className="text-[12px] mt-[1px] shrink-0" />
              <span>Only super admins can modify an admin or super admin's role.</span>
            </div>
          )}
          <div className="flex items-center gap-2 mb-1.5">
            <div className="flex-1 relative">
              <FiShield className="absolute left-3 top-1/2 -translate-y-1/2 text-[12px] text-[#7a7060] pointer-events-none" />
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                disabled={sectionLocked}
                className={`w-full appearance-none border rounded-[9px] pl-8 pr-3 py-2 text-[12px] outline-none transition-all focus:shadow-sm focus:border-[#145f5f] ${sectionLocked ? 'bg-[#f8f5ef] cursor-not-allowed opacity-60' : 'bg-white cursor-pointer'}`}
                style={{ borderColor: '#ddd6c6' }}
              >
                {roles.map((r) => {
                  const locked = isRoleOptionLocked(r.name);
                  return (
                    <option key={r.name || r._id} value={r.name} disabled={locked}>
                      {r.displayName || r.name}{locked ? ' (locked)' : ''}
                    </option>
                  );
                })}
              </select>
            </div>
            <Button
              loading={actionBusy === 'changeRole'}
              disabled={updateRoleDisabled}
              onClick={() => handleAction('changeRole', selectedRole)}
            >
              Update Role
            </Button>
          </div>
          {!viewerIsSuperAdmin && !sectionLocked && (
            <p className="text-[10px] text-[#7a7060] mb-2.5 flex items-center gap-1">
              <FiInfo className="text-[10px]" />
              Admin and super admin roles can only be assigned by a super admin.
            </p>
          )}

          {/* Action buttons - equal-width grid */}
          <div className="grid grid-cols-3 gap-2">
            <Button
              variant={isActive ? 'orange' : 'success'}
              block
              loading={actionBusy === 'toggleStatus'}
              disabled={deactivateDisabled}
              title={deactivateBlockReason || undefined}
              leftIcon={deactivateBlockReason
                ? <FiLock className="text-[12px]" />
                : isActive ? <FiUserX className="text-[12px]" /> : <FiUserCheck className="text-[12px]" />}
              onClick={() => handleAction('toggleStatus')}
            >
              {isActive ? 'Deactivate' : 'Activate'}
            </Button>
            <Button
              variant="blue"
              block
              loading={actionBusy === 'revokeSessions'}
              disabled={revokeDisabled}
              title={revokeBlockReason || (viewerIsSelf ? 'Sign yourself out of all sessions' : undefined)}
              leftIcon={revokeBlockReason ? <FiLock className="text-[12px]" /> : <FiLogOut className="text-[12px]" />}
              onClick={() => handleAction('revokeSessions')}
            >
              Revoke
            </Button>
            <Button
              variant="danger"
              block
              loading={actionBusy === 'delete'}
              disabled={deleteDisabled}
              title={deleteBlockReason || undefined}
              leftIcon={deleteBlockReason ? <FiLock className="text-[12px]" /> : <FiTrash2 className="text-[12px]" />}
              onClick={() => handleAction('delete')}
            >
              Delete
            </Button>
          </div>

        </div>

        {flagModalOpen && (
          <FlagUserModal
            user={user}
            onClose={() => setFlagModalOpen(false)}
            onFlagged={() => setOpenFlagCount((c) => c + 1)}
          />
        )}
      </div>
    </div>
  );
}

export default function AdminUsersPage() {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Mount always starts at page 1 with empty search/role; the status filter can
  // arrive as ?filter=locked. Seed the SWR state from that default-view key so a
  // revisit renders the table instantly instead of flashing "Loading users...".
  const initialStatus = searchParams.get('filter') === 'locked' ? 'locked' : '';
  const seedKey = usersKey(1, '', '', initialStatus);
  const seeded = getView(seedKey);
  const [users, setUsers] = useState(() => seeded?.users ?? []);
  const [pagination, setPagination] = useState(() => seeded?.pagination ?? {});
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [loading, setLoading] = useState(() => !hasView(seedKey));
  const [roles, setRoles] = useState(() => getView(ROLES_KEY) ?? []);
  const [selectedUser, setSelectedUser] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [toast, setToast] = useState('');
  const [page, setPage] = useState(1);
  const [activeSuperAdminCount, setActiveSuperAdminCount] = useState(() => seeded?.activeSuperAdminCount ?? null);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const fetchUsers = useCallback(async (p = 1) => {
    const key = usersKey(p, search, roleFilter, statusFilter);
    // Full gate only for a view we've never loaded; a cached one (revisit, or a
    // page/filter seen before) refreshes silently underneath.
    if (!hasView(key)) setLoading(true);
    try {
      const params = { page: p, limit: 15 };
      if (search) params.search = search;
      if (roleFilter) params.role = roleFilter;
      if (statusFilter === 'locked') params.locked = 'true';
      else if (statusFilter) params.isActive = statusFilter;
      const res = await usersAPI.getAll(params);
      if (res.success) {
        const list = res.data?.users || [];
        const pg = res.data?.pagination || {};
        const superCount = typeof res.data?.activeSuperAdminCount === 'number'
          ? res.data.activeSuperAdminCount
          : null;
        setUsers(list);
        setPagination(pg);
        if (superCount !== null) setActiveSuperAdminCount(superCount);
        setView(key, { users: list, pagination: pg, activeSuperAdminCount: superCount });
      }
    } catch (err) {
      // Keep the cached page on a failed refresh.
      console.error('Fetch users error:', err);
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter, statusFilter]);

  useEffect(() => { fetchUsers(page); }, [fetchUsers, page]);

  // Deep links drive the status filter (?filter=locked — the sidebar's
  // "Account Lockouts" entry and the lockout alert both land here), including
  // when the link is clicked while already on this page.
  useEffect(() => {
    if (searchParams.get('filter') === 'locked') {
      setStatusFilter('locked');
      setPage(1);
    }
  }, [searchParams]);

  useEffect(() => {
    const loadRoles = async () => {
      try {
        const res = await rolesAPI.getAll();
        if (res.success) {
          const list = res.data?.roles || [];
          setRoles(list);
          setView(ROLES_KEY, list);
        }
      } catch (_) { /* roles API may not be available */ }
    };
    loadRoles();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUsers(1);
  };

  const refreshAfterAction = async (userId) => {
    fetchUsers(page);
    if (selectedUser && (selectedUser._id || selectedUser.id) === userId) {
      try {
        const res = await usersAPI.getById(userId);
        if (res.success) setSelectedUser(res.data?.user || null);
      } catch (_) { setSelectedUser(null); }
    }
  };

  const handleUserAction = async (action, userId, payload) => {
    try {
      switch (action) {
        case 'toggleStatus': {
          const target = users.find((u) => (u._id || u.id) === userId);
          const newStatus = target?.isActive === false;
          await usersAPI.updateStatus(userId, newStatus);
          showToast(`User ${newStatus ? 'activated' : 'deactivated'} successfully`);
          break;
        }
        case 'changeRole':
          // Loud confirmation when granting super admin — these grants are
          // unrestricted and should be a deliberate action.
          if (payload === 'super_admin') {
            const targetUser = users.find((u) => (u._id || u.id) === userId) || selectedUser;
            setConfirm({
              title: 'Promote to Super Admin?',
              message: `Super admins have unrestricted access — they can manage all users, change roles, and modify any data in the system. Grant this to ${targetUser?.fullName || 'this user'}?`,
              confirmLabel: 'Yes, promote',
              danger: false,
              onConfirm: async () => {
                try {
                  await usersAPI.changeRole(userId, payload);
                  showToast('User promoted to super admin');
                  setConfirm(null);
                  await refreshAfterAction(userId);
                } catch (err) {
                  setConfirm(null);
                  showToast(`Error: ${err.message}`);
                }
              },
            });
            return;
          }
          await usersAPI.changeRole(userId, payload);
          showToast('User role updated successfully');
          break;
        case 'revokeSessions':
          await usersAPI.revokeSessions(userId);
          showToast('User sessions revoked successfully');
          break;
        case 'delete':
          setConfirm({
            title: 'Delete User',
            message: 'Are you sure you want to permanently delete this user? This action cannot be undone.',
            confirmLabel: 'Delete',
            danger: true,
            onConfirm: async () => {
              try {
                await usersAPI.delete(userId);
                showToast('User deleted successfully');
                setConfirm(null);
                setSelectedUser(null);
                fetchUsers(page);
              } catch (err) {
                setConfirm(null);
                showToast(`Error: ${err.message}`);
              }
            },
          });
          return; // Don't refresh yet
        default:
          break;
      }
      await refreshAfterAction(userId);
    } catch (err) {
      showToast(`Error: ${err.message}`);
    }
  };

  return (
    <AppLayout pageTitle="User Management">
      <div className="p-6 max-w-[1200px] mx-auto">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <h1 className="text-[24px] font-bold text-[#08111f]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>User Management</h1>
            <p className="text-[13px] text-[#7a7060]">{pagination.totalUsers || 0} total users registered</p>
          </div>
          {/* Refresh — refetch the current page with current filters. Same
              pattern as CompaniesTab / OrdersTab so every admin surface has
              the affordance in the same spot (right of the heading). */}
          <RefreshButton onClick={() => fetchUsers(page)} loading={loading} />
        </div>

        {/* Tab navigation removed — the admin area is sidebar-driven now
            (same treatment as AdminDashboardPage). */}

        {/* Search & Filters */}
        <form onSubmit={handleSearch} className="flex flex-wrap gap-3 mb-4">
          <input
            type="text"
            placeholder="Search by name, email, or mobile..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 min-w-[200px] border-[1.5px] rounded-[9px] px-3 py-[9px] text-[12.5px] outline-none transition-shadow focus:shadow-sm focus:border-[#145f5f40]"
            style={{ borderColor: '#ddd6c6' }}
          />
          <select value={roleFilter} onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
            className="border-[1.5px] rounded-[9px] px-3 py-[9px] text-[12.5px] outline-none transition-colors hover:border-[#b8b0a0]" style={{ borderColor: '#ddd6c6' }}>
            <option value="">All Roles</option>
            {roles.map((r) => <option key={r.name || r._id} value={r.name}>{r.displayName || r.name}</option>)}
            {roles.length === 0 && <>
              <option value="professional">Professional</option>
              <option value="corporate">Corporate</option>
              <option value="admin">Admin</option>
              <option value="super_admin">Super Admin</option>
            </>}
          </select>
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="border-[1.5px] rounded-[9px] px-3 py-[9px] text-[12.5px] outline-none transition-colors hover:border-[#b8b0a0]" style={{ borderColor: '#ddd6c6' }}>
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
            <option value="locked">Locked</option>
          </select>
          <Button type="submit">Search</Button>
        </form>

        {/* Toast */}
        {toast && (
          <div className="mb-4 text-[12.5px] px-3 py-2 rounded-lg border animate-pulse" style={{ background: '#e8efe5', borderColor: '#d3e2cb', color: '#4a6b3f' }}>{toast}</div>
        )}

        {/* Users Table */}
        <div className="bg-white rounded-[12px] border overflow-hidden" style={{ borderColor: '#ddd6c6', boxShadow: '0 2px 16px rgba(8,17,31,.07)' }}>
          <div className="overflow-x-auto cs-table-scroll">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-[#7a7060]" style={{ background: '#f8f5ef' }}>
                  <th className="text-left py-3 px-4">Name</th>
                  <th className="text-left py-3 px-4">Email</th>
                  <th className="text-left py-3 px-4">Mobile</th>
                  <th className="text-left py-3 px-4">Role</th>
                  <th className="text-left py-3 px-4">Status</th>
                  <th className="text-left py-3 px-4">Verified</th>
                  <th className="text-left py-3 px-4">Last Login</th>
                  <th className="text-left py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="py-12 text-center text-[#7a7060]">Loading users…</td></tr>
                ) : users.length === 0 ? (
                  <tr><td colSpan={8} className="py-12 text-center text-[#7a7060]">No users found</td></tr>
                ) : users.map((u) => {
                  const isSelf = (u._id || u.id) === (currentUser?.id || currentUser?._id);
                  const viewerIsSuper = currentUser?.roleName === 'super_admin';
                  const targetIsPrivileged = ['admin', 'super_admin'].includes(u.roleName);
                  const isLastSuperRow =
                    u.roleName === 'super_admin' &&
                    u.isActive !== false &&
                    typeof activeSuperAdminCount === 'number' &&
                    activeSuperAdminCount <= 1;
                  const rowDeactivateBlocked =
                    isSelf || (targetIsPrivileged && !viewerIsSuper) || (u.isActive !== false && isLastSuperRow);
                  const rowDeactivateReason = isSelf
                    ? "Can't deactivate your own account"
                    : (targetIsPrivileged && !viewerIsSuper)
                      ? 'Only super admins can deactivate an admin or super admin'
                      : (u.isActive !== false && isLastSuperRow)
                        ? 'Cannot deactivate the last active super admin'
                        : null;
                  return (
                    <tr
                      key={u._id || u.id}
                      onClick={() => setSelectedUser(u)}
                      className="border-t cursor-pointer transition-colors hover:bg-[#f8f5ef]"
                      style={{ borderColor: '#ddd6c6' }}
                    >
                      <td className="py-3 px-4 font-medium text-[#08111f]">{u.fullName}</td>
                      <td className="py-3 px-4 text-[#7a7060]">{u.email}</td>
                      <td className="py-3 px-4 text-[#7a7060]">{u.mobile}</td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: '#145f5f22', color: '#145f5f' }}>
                          {u.roleName || 'user'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${u.isActive !== false ? 'bg-[#e8efe5] text-[#4a6b3f]' : 'bg-[#f2e9e6] text-[#9a564a]'}`}>
                          {u.isActive !== false ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`text-[11px] ${u.isPhoneVerified ? 'text-[#4a6b3f]' : 'text-[#7a7060]'}`}>
                          {u.isPhoneVerified ? '\u2713 Yes' : 'No'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-[#7a7060] text-[11px]">
                        {u.lastLogin ? new Date(u.lastLogin).toLocaleDateString('en-IN') : 'Never'}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1.5">
                          <ViewButton onClick={(e) => { e.stopPropagation(); setSelectedUser(u); }} title="View user" />
                          <Button
                            variant={u.isActive !== false ? 'orange' : 'success'}
                            size="xs"
                            disabled={rowDeactivateBlocked}
                            title={rowDeactivateReason || undefined}
                            onClick={(e) => { e.stopPropagation(); if (!rowDeactivateBlocked) handleUserAction('toggleStatus', u._id || u.id); }}
                          >
                            {u.isActive !== false ? 'Deactivate' : 'Activate'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <span className="text-[12px] text-[#7a7060]">
              Page {pagination.currentPage || page} of {pagination.totalPages}
            </span>
            <div className="flex gap-2">
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1.5 rounded-[9px] border text-[12px] font-semibold disabled:opacity-40 transition-colors hover:bg-[#f8f5ef]" style={{ borderColor: '#ddd6c6' }}>
                &larr; Prev
              </button>
              <button disabled={page >= pagination.totalPages} onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 rounded-[9px] border text-[12px] font-semibold disabled:opacity-40 transition-colors hover:bg-[#f8f5ef]" style={{ borderColor: '#ddd6c6' }}>
                Next &rarr;
              </button>
            </div>
          </div>
        )}
      </div>

      {/* User Detail Modal */}
      <UserDetailModal
        user={selectedUser}
        roles={roles}
        onClose={() => setSelectedUser(null)}
        onAction={handleUserAction}
        viewerRoleName={currentUser?.roleName}
        viewerId={currentUser?.id || currentUser?._id}
        activeSuperAdminCount={activeSuperAdminCount}
        onUserUpdated={(patch) => {
          if (!patch) return;
          // After mark-compromised the response carries the new isActive/compromisedAt.
          // Reflect immediately in the open modal and trigger a refresh of the list.
          setSelectedUser((prev) => (prev ? { ...prev, ...patch } : prev));
          fetchUsers?.(page);
        }}
      />

      {/* Confirm Modal */}
      <ConfirmModal open={!!confirm} title={confirm?.title} message={confirm?.message} confirmLabel={confirm?.confirmLabel} danger={confirm?.danger}
        onConfirm={confirm?.onConfirm} onCancel={() => setConfirm(null)} />
    </AppLayout>
  );
}
